import React, { Dispatch, MouseEventHandler, SetStateAction, useEffect } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import styled, { css, useTheme } from 'styled-components';

// 아이콘
import CorePortal from './CorePortal';
import CoreSvg from './CoreSvg';
import Icon from './icons';

interface IToast {
	text: string;
	permanent: boolean;
	is_error?: boolean;
	mount: boolean;
	setMount: Dispatch<SetStateAction<boolean>> | ((value: boolean) => void);
	_onClick?: MouseEventHandler<HTMLDivElement>;
	max_width?: string;
}

const CoreToast = ({
	text,
	permanent,
	is_error,
	mount,
	setMount,
	_onClick,
	max_width,
}: IToast) => {
	// permanent가 아닌 경우 3초 후 자동으로 사라진다
	useEffect(() => {
		if (permanent) return;

		setTimeout(() => {
			setMount(false);
		}, 5000);
	}, [mount]);

	const theme = useTheme();
	return (
		<CorePortal>
			<AnimatePresence>
				{mount && (
					<ToastBox
						initial={{ x: '-50%', y: '32px' }}
						animate={{
							x: '-50%',
							y: '-88px',
						}}
						transition={{ ease: 'easeOut', duration: 0.4 }}
						exit={{ x: '-50%', y: '32px' }}
						// eslint-disable-next-line @typescript-eslint/no-empty-function
						onClick={permanent ? _onClick : () => {}}
						clickable={permanent ? 'true' : 'false'}
						is_error={is_error ? 'true' : 'false'}
						max_width={max_width}
					>
						{is_error && (
							<CoreSvg
								_margin="0 8px 0 0"
								_fill={theme.colors.system.red_darken}
								_onClick={() => {
									setMount(false);
								}}
							>
								<Icon.Warning />
							</CoreSvg>
						)}
						{text}
						{
							<CoreSvg
								_margin="0 0 0 8px"
								_fill={
									is_error ? theme.colors.system.red_darken : theme.colors.gray.gray_white
								}
								_onClick={() => {
									setMount(false);
								}}
							>
								<Icon.Close />
							</CoreSvg>
						}
					</ToastBox>
				)}
			</AnimatePresence>
		</CorePortal>
	);
};

const ToastBox = styled(motion.div)<{
	clickable: 'true' | 'false';
	is_error: 'true' | 'false';
	max_width?: string;
}>`
	width: max-content;
	${({ max_width }) => max_width && `max-width: ${max_width}`};
	white-space: pre-wrap;
	/* height: 56px; */
	padding: 16px;
	background-color: ${({ theme }) => theme.colors.gray.gray80_text};
	border-radius: 8px;

	${({ theme }) => theme.typo.body.p_bold};
	color: ${({ theme }) => theme.colors.gray.gray_white};

	${({ is_error, theme }) =>
		is_error === 'true' &&
		css`
			background-color: ${theme.colors.system.red_lighten};
			color: ${theme.colors.gray.gray80_text};
			font-weight: 700;
		`}

	position: fixed;
	bottom: -56px;
	left: 50%;
	z-index: 100000;
	transform: translateX(-50%);

	box-shadow: ${({ theme }) => theme.shadow.depth04};
	cursor: ${({ clickable }) => clickable === 'true' && 'pointer'};

	${({ theme }) => theme.common.flexCenter};
`;

const IconWrapper = styled.div`
	width: 24px;
	height: 24px;
	margin-left: 4px;

	cursor: pointer;

	& > span {
		width: 100%;
		height: 100%;
	}
`;

export default CoreToast;

// props
//
// - text: toast에 들어갈 텍스트
// - permanent: 3초 후에 사라지는지, 계속 남아있는 지의 여부. true일 경우 클릭이 가능하다.
// -_onClick: permanent가 false일 경우, 이벤트는 적용되지 않는다.
